import { useEffect, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react"
import { FADE_MS, prefersReducedMotion } from "../mount/createShadowHost"
import type { EmbedUi } from "../types"
import { IconClose, IconSearch } from "./icons"
import { resultOptionId } from "./ResultsList"

type AutocompleteShellProps = {
  variant: EmbedUi
  ariaLabel: string
  containerRef: RefObject<HTMLDivElement | null>
  inputRef: RefObject<HTMLInputElement | null>
  /** Ties the input to the listbox it controls; must match the `ResultsList`. */
  listboxId: string
  highlightedIndex: number
  placeholder: string
  query: string
  onQueryChange: (value: string) => void
  onKeyDown: (event: KeyboardEvent) => void
  onFocus: () => void
  /** Fired when focus leaves the whole widget — dropdown uses it to collapse. */
  onFocusLeave: () => void
  onClose: () => void
  toolbar?: ReactNode
  showResults: boolean
  results: ReactNode
  footer?: ReactNode
}

/**
 * Shared chrome for both autocomplete surfaces. `lightbox` renders the modal
 * dialog; `dropdown` renders a SearchHero-style pill with a panel beneath it.
 */
export function AutocompleteShell({
  variant,
  ariaLabel,
  containerRef,
  inputRef,
  listboxId,
  highlightedIndex,
  placeholder,
  query,
  onQueryChange,
  onKeyDown,
  onFocus,
  onFocusLeave,
  onClose,
  toolbar,
  showResults,
  results,
  footer,
}: AutocompleteShellProps) {
  const isDropdown = variant === "dropdown"

  const header = (
    <div className="bpp-modal-header">
      <IconSearch />
      <input
        ref={inputRef}
        className="bpp-input"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        // Escape collapses the dropdown but keeps focus, so a plain click has to
        // be able to reopen it — no focus event fires on an already-focused input.
        onClick={onFocus}
        placeholder={placeholder}
        autoComplete="off"
        aria-label={ariaLabel}
        // `aria-expanded` and `aria-activedescendant` are only valid on a
        // combobox, and without the latter a screen reader announces nothing as
        // the visitor arrows through results — focus never leaves the input.
        role="combobox"
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-expanded={showResults}
        aria-activedescendant={
          showResults && highlightedIndex >= 0
            ? resultOptionId(listboxId, highlightedIndex)
            : undefined
        }
      />
      {!isDropdown && (
        <button type="button" className="bpp-icon-button" onClick={onClose} aria-label="Close">
          <IconClose />
        </button>
      )}
    </div>
  )

  if (isDropdown) {
    return (
      <div
        ref={containerRef}
        className="bpp-dropdown"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            onFocusLeave()
          }
        }}
      >
        {toolbar}
        {header}
        <FadePanel show={showResults}>
          {results}
          {footer}
        </FadePanel>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="bpp-modal" role="dialog" aria-modal="true" aria-label={ariaLabel}>
      {toolbar}
      {header}
      {results}
      {footer}
    </div>
  )
}

/** Keeps the panel mounted through the fade-out. Opacity itself lives in CSS. */
function FadePanel({ show, children }: { show: boolean; children: ReactNode }) {
  const reduceMotion = prefersReducedMotion()
  const [mounted, setMounted] = useState(show)
  const [visible, setVisible] = useState(show && reduceMotion)

  useEffect(() => {
    if (show) {
      setMounted(true)
      if (reduceMotion) {
        setVisible(true)
        return
      }
      const frame = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(frame)
    }
    if (reduceMotion) {
      setVisible(false)
      setMounted(false)
      return
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), FADE_MS)
    return () => window.clearTimeout(timer)
  }, [show, reduceMotion])

  if (!mounted) {
    return null
  }

  return (
    <div
      className="bpp-dropdown-panel"
      data-visible={visible ? "true" : "false"}
      onMouseDown={(event) => event.preventDefault()}
    >
      {children}
    </div>
  )
}
